import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { BELTS, findBelt } from "./cardsData";
import { PageLoader } from "../Loader/loader";
import { fullName } from "../../utils/fullName";

const PLACE_TYPES = {
  schools: "Դպրոց",
  clubs: "Ակումբ",
  federation: "Մարզային ֆեդերացիա",
};

const Person = ({ person, role, onOpen }) => {
  const belt = findBelt(person.belt);
  const badge = person.is_me ? "Դուք" : person.is_my_trainer ? "Իմ մարզիչը" : null;

  return (
    <li className={`team-card${person.is_me ? " me" : ""}`}>
      <button type="button" className="team-card-btn" onClick={() => onOpen(person, role)}>
      <div className="team-photo">
        <img src={person.image_url} alt="" loading="lazy" decoding="async" />
      </div>
      <div className="team-name">
        <strong>
          {fullName(person)}
        </strong>
        {role === "fighter" && belt && <span className="team-belt-name">{belt.label} գոտի</span>}
        {badge && <span className="team-badge">{badge}</span>}
      </div>
      {role === "fighter" && belt && (
        <span
          className="team-belt"
          style={{ "--belt": belt.color }}
          title={`Գոտի՝ ${belt.label}`}
          aria-label={`Գոտի՝ ${belt.label}`}
        />
      )}
      </button>
    </li>
  );
};

// Details of one person: a centered dialog on desktop, a bottom sheet on phones.
const PersonDialog = ({ person, role, team, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const belt = findBelt(person.belt);
  const fullName = [person.firstname, person.surname].join(" ");
  const trainer = team.trainers.find((t) => t.id === person.trainer_id);
  const fighterCount = team.fighters.filter((f) => f.trainer_id === person.id).length;
  const badge = person.is_me ? "Դուք" : person.is_my_trainer ? "Իմ մարզիչը" : null;

  const rows = [
    ["Դեր", role === "trainer" ? "Մարզիչ" : "Մարզիկ"],
    role === "trainer" && ["Մարզիկներ", fighterCount],
    role === "fighter" && trainer && ["Մարզիչ", fullName(trainer)],
    ["Կազմակերպություն", team.place.name],
  ].filter(Boolean);

  return createPortal(
    <div className="person-modal" onClick={onClose}>
      <div
        className="person-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={fullName}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="person-sheet-close" aria-label="Փակել" onClick={onClose}>
          &times;
        </button>
        <img className="person-sheet-photo" src={person.photo_url || person.image_url} alt="" />
        <h3 className="person-sheet-name">{fullName}</h3>
        {person.patronymic && <p className="person-sheet-sub">{person.patronymic}</p>}
        {badge && <span className="team-badge">{badge}</span>}
        {role === "fighter" && belt && (
          <div className="person-sheet-belt">
            <span className="team-belt" style={{ "--belt": belt.color }} />
            <span>{belt.label} գոտի</span>
          </div>
        )}
        <dl className="person-sheet-rows">
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>,
    document.body
  );
};

// "My team": the trainers and fighters of the member's own school/club.
const Team = () => {
  const { fetchTeam } = useAuth();
  const [team, setTeam] = useState(null);
  const [error, setError] = useState(false);
  const [beltFilter, setBeltFilter] = useState("all");
  const [selected, setSelected] = useState(null); // { person, role }

  useEffect(() => {
    let cancelled = false;
    fetchTeam()
      .then((data) => !cancelled && setTeam(data))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="account-panel">
        <h2 className="account-title">Իմ թիմը</h2>
        <div className="auth-alert">Չհաջողվեց բեռնել թիմը։ Փորձեք կրկին։</div>
      </div>
    );
  }

  if (!team) return <PageLoader compact />;

  if (!team.place) {
    return (
      <div className="account-panel">
        <h2 className="account-title">Իմ թիմը</h2>
        <p className="auth-muted">
          Կազմակերպությունը նշված չէ։ <Link to="/profile/settings">Ընտրել կազմակերպություն</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="account-panel">
      <h2 className="account-title">Իմ թիմը</h2>
      <p className="team-place">
        <strong>{team.place.name}</strong>
        {PLACE_TYPES[team.place.type] && <span> · {PLACE_TYPES[team.place.type]}</span>}
        {team.place.location && <span className="auth-muted"> · {team.place.location}</span>}
      </p>

      <h3 className="account-subtitle">Մարզիչներ ({team.trainers.length})</h3>
      {team.trainers.length ? (
        <ul className="team-grid">
          {team.trainers.map((p) => (
            <Person key={p.id} person={p} role="trainer" onOpen={(person, role) => setSelected({ person, role })} />
          ))}
        </ul>
      ) : (
        <p className="auth-muted">Մարզիչներ դեռ չկան։</p>
      )}

      <h3 className="account-subtitle">Մարզիկներ ({team.fighters.length})</h3>
      {team.fighters.length > 0 && (
        <div className="belt-filter" role="group" aria-label="Գոտու գույն">
          <button
            type="button"
            className={`belt-chip${beltFilter === "all" ? " active" : ""}`}
            onClick={() => setBeltFilter("all")}
          >
            Բոլորը <b>{team.fighters.length}</b>
          </button>
          {BELTS.map((belt) => {
            const count = team.fighters.filter((p) => p.belt === belt.key).length;
            if (!count) return null;
            return (
              <button
                key={belt.key}
                type="button"
                className={`belt-chip${beltFilter === belt.key ? " active" : ""}`}
                onClick={() => setBeltFilter(belt.key)}
              >
                <span className="belt-chip-dot" style={{ background: belt.color }} />
                {belt.label} <b>{count}</b>
              </button>
            );
          })}
        </div>
      )}
      {team.fighters.length ? (
        <ul className="team-grid">
          {team.fighters
            .filter((p) => beltFilter === "all" || p.belt === beltFilter)
            .map((p) => (
              <Person key={p.id} person={p} role="fighter" onOpen={(person, role) => setSelected({ person, role })} />
            ))}
        </ul>
      ) : (
        <p className="auth-muted">Մարզիկներ դեռ չկան։</p>
      )}

      {selected && (
        <PersonDialog
          person={selected.person}
          role={selected.role}
          team={team}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default Team;
